import React, { useState } from 'react';
import { Modal, Form, Input, Button, Select, DatePicker, Space, Avatar } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/pro-solid-svg-icons';
import dayjs from 'dayjs';
import { PlannerPage } from '../../../services/tasks';
import { createTask, CreateTaskRequest } from '../../../services/tasks';
import { getUsers } from '../../../services/users';

const { TextArea } = Input;
const { Option } = Select;

interface CreateTaskModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  plannerPage: PlannerPage;
  initialStatus?: string;
  initialSwimlane?: string;
  initialDueDate?: string;
}

const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
  open,
  onClose,
  onSuccess,
  plannerPage,
  initialStatus,
  initialSwimlane,
  initialDueDate,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: getUsers,
    enabled: open,
  });

  React.useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      status: initialStatus || plannerPage.statuses[0],
      swimlane: initialSwimlane,
      priority: 'MEDIUM',
      dueDate: initialDueDate ? dayjs(initialDueDate) : undefined,
    });
  }, [form, initialDueDate, initialStatus, initialSwimlane, open, plannerPage.id, plannerPage.statuses]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const taskData: CreateTaskRequest = {
        title: values.title,
        description: values.description,
        status: values.status || plannerPage.statuses[0],
        priority: values.priority || 'MEDIUM',
        dueDate: values.dueDate ? values.dueDate.toISOString() : undefined,
        tags: values.tags ? values.tags.split(',').map((t: string) => t.trim()) : undefined,
        assigneeId: values.assigneeId || undefined,
        plannerPageId: plannerPage.id,
        swimlane: values.swimlane || undefined,
      };

      setLoading(true);
      await createTask(taskData);
      form.resetFields();
      onSuccess();
    } catch (error) {
      console.error('Failed to create task:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Create New Task"
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit} loading={loading}>
          Create Task
        </Button>,
      ]}
      width={600}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="title"
          label="Task Title"
          rules={[{ required: true, message: 'Please enter a task title' }]}
        >
          <Input placeholder="Enter task title" />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <TextArea rows={4} placeholder="Add task description" />
        </Form.Item>

        <Form.Item
          name="status"
          label="Status"
          initialValue={plannerPage.statuses[0]}
        >
          <Select>
            {plannerPage.statuses.map((status) => (
              <Option key={status} value={status}>
                {status}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {(plannerPage.swimlanes?.length ?? 0) > 0 && (
          <Form.Item name="swimlane" label="Swimlane">
            <Select placeholder="Select swimlane" allowClear>
              <Option value="">Unassigned</Option>
              {plannerPage.swimlanes!.map((sl) => (
                <Option key={`${sl.type}:${sl.value}`} value={`${sl.type}:${sl.value}`}>
                  {sl.type}: {sl.value}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item name="assigneeId" label="Assignee">
          <Select
            className="assignee-search-select"
            placeholder="Select assignee"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const label = option?.label as string;
              return label?.toLowerCase().includes(input.toLowerCase()) ?? false;
            }}
            options={users.map((user) => ({
              value: user.id,
              label: user.name,
              user: user,
            }))}
            optionRender={(option) => (
              <Space>
                <Avatar size="small" src={option.data.user?.photoUrl} icon={<FontAwesomeIcon icon={faUser} />} />
                {option.data.label}
              </Space>
            )}
          />
        </Form.Item>

        <Space style={{ width: '100%' }} size="middle">
          <Form.Item
            name="priority"
            label="Priority"
            initialValue="MEDIUM"
            style={{ flex: 1 }}
          >
            <Select>
              <Option value="LOW">Low</Option>
              <Option value="MEDIUM">Medium</Option>
              <Option value="HIGH">High</Option>
              <Option value="URGENT">Urgent</Option>
            </Select>
          </Form.Item>

          <Form.Item name="dueDate" label="Due Date" style={{ flex: 1 }}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Space>

        <Form.Item name="tags" label="Tags">
          <Input placeholder="Comma-separated tags (e.g., frontend, urgent, bug)" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateTaskModal;
